import { LinkSuggestions } from "./LinkSuggestions.js";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { buildZodSchema, type EntitySpec, type FieldSpec } from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { brl, ErrorBox } from "./ui.js";
import { parseChecklist, type ChecklistState } from "../lib/checklist.js";

const pad2 = (n: number) => String(n).padStart(2, "0");
const PLACAS = ["cavalo", "primeira_carreta", "dolly", "segunda_carreta"];

function useDebounced(value: string) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), 250);
    return () => clearTimeout(timer);
  }, [value]);
  return debounced;
}

// <input type="date"> só aceita "YYYY-MM-DD"; qualquer outra coisa renderiza
// vazio. O Postgres devolve DATE como `Date` (meia-noite LOCAL), então lemos
// com getters locais para não voltar um dia.
function toDateInput(v: unknown): string {
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return "";
    return `${v.getFullYear()}-${pad2(v.getMonth() + 1)}-${pad2(v.getDate())}`;
  }
  const s = String(v).trim();
  return s.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? "";
}

// <input type="datetime-local"> espera "YYYY-MM-DDTHH:mm".
function toDateTimeInput(v: unknown): string {
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return "";
    return `${toDateInput(v)}T${pad2(v.getHours())}:${pad2(v.getMinutes())}`;
  }
  const s = String(v).trim().replace(" ", "T");
  const m = s.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : toDateInput(v) ? `${toDateInput(v)}T00:00` : "";
}

// Editor de checklist (materiais do motorista): por item, um "confere" +
// quantidade. Serializa em JSON string ({ "Cinta": {ok,qtd} }) para o catálogo.
function ChecklistInput({ field, value, onChange }: { field: FieldSpec; value: string; onChange: (v: string) => void }) {
  const items = field.items ?? [];
  const state: ChecklistState = parseChecklist(value);
  const update = (item: string, patch: Partial<{ ok: boolean; qtd: number }>) => {
    const prev = state[item] ?? { ok: false, qtd: 0 };
    const next: ChecklistState = { ...state, [item]: { ...prev, ...patch } };
    onChange(JSON.stringify(next));
  };
  return (
    <div className="space-y-1.5 rounded-lg border border-slate-200 bg-slate-50 p-3">
      {items.map((item) => {
        const s = state[item] ?? { ok: false, qtd: 0 };
        return (
          <div key={item} className="flex items-center gap-3">
            <label className="flex flex-1 items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!s.ok}
                onChange={(e) => update(item, { ok: e.target.checked, qtd: e.target.checked && !s.qtd ? 1 : s.qtd })}
              />
              {item}
            </label>
            <input
              className="input h-8 w-20 py-1 text-sm"
              type="number"
              min={0}
              value={s.qtd || ""}
              placeholder="qtd"
              onChange={(e) => update(item, { qtd: Number(e.target.value) || 0, ok: (Number(e.target.value) || 0) > 0 || s.ok })}
            />
          </div>
        );
      })}
    </div>
  );
}

// Busca opções para um campo "reference" (autocomplete via datalist).
function ReferenceInput({ field, value, onChange }: { field: FieldSpec; value: string; onChange: (v: string) => void }) {
  const ref = field.ref!;
  const termo = useDebounced(value);
  const { data, isError } = useQuery({
    queryKey: ["ref", ref.entity, termo],
    queryFn: async () => {
      const qs = `limit=500&q=${encodeURIComponent(termo)}`;
      const entidades = ref.entity === "frota" ? ["frota", "curso_autor"] : [ref.entity];
      const resultados = await Promise.all(entidades.map(async (entity) => {
        const rows = (await api.get(`/entities/${entity}?${qs}`)).data.rows as Record<string, unknown>[];
        return rows.map((r) => ({
          value: String(r[entity === "curso_autor" && ref.entity === "frota" ? "nome" : ref.valueField] ?? ""),
          label: String(r[entity === "curso_autor" && ref.entity === "frota" ? "nome" : ref.labelField] ?? ""),
        }));
      }));
      return [...new Map(resultados.flat().filter((r) => r.value).map((r) => [r.value, r])).values()];
    },
  });
  const listId = `dl-${field.name}`;
  return (
    <>
      <input className="input" list={listId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.hint} />
      <datalist id={listId}>
        {(data ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </datalist>
      {isError && <p className="text-xs text-amber-700">Não foi possível carregar as sugestões. Você pode preencher o campo.</p>}
    </>
  );
}

function Field({ field, value, onChange }: { field: FieldSpec; value: string; onChange: (v: string) => void }) {
  if (field.type === "enum") {
    // Valor legado fora da lista (histórico da planilha) entra como opção
    // extra. Sem isto o <select> ficava com selectedIndex = -1 (em branco), o
    // formulário reenviava o valor inválido e o Zod barrava o "Salvar" — a
    // linha inteira virava somente-leitura. Rotulamos para o operador ver que
    // é um valor antigo e poder trocar.
    const opcoes = field.options ?? [];
    const legado = value !== "" && !opcoes.includes(value);
    return (
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {legado && <option value={value}>{value} (valor antigo)</option>}
        {opcoes.map((o) => <option key={o} value={o}>{o || "(vazio)"}</option>)}
      </select>
    );
  }
  if (field.ref) return <ReferenceInput field={field} value={value} onChange={onChange} />;
  if (field.type === "checklist") return <ChecklistInput field={field} value={value} onChange={onChange} />;
  if (field.type === "textarea") return <textarea className="input" rows={2} value={value} onChange={(e) => onChange(e.target.value)} />;
  if (field.type === "boolean") return <input type="checkbox" checked={value === "true"} onChange={(e) => onChange(String(e.target.checked))} />;
  const inputType = field.type === "date" ? "date" : field.type === "datetime" ? "datetime-local" : field.type === "number" || field.type === "currency" ? "number" : "text";
  return <input className="input" type={inputType} step="any" value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.hint} />;
}

export function FormEngine({
  spec, initial = {}, onSubmit, onCancel, submitting, mode = "create",
}: {
  spec: EntitySpec;
  initial?: Record<string, unknown>;
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  submitting?: boolean;
  /** "update" = PATCH parcial (só o que mudou). Ver handleSubmit. */
  mode?: "create" | "update";
}) {
  const editable = spec.fields.filter((f) => !f.computed);
  const computed = spec.fields.filter((f) => f.computed);
  // Serializa o valor vindo da API para o formato que CADA input aceita.
  //  - jsonb (checklist) vira JSON string — String() de objeto daria "[object Object]".
  //  - date/datetime: o Postgres devolve `Date`, e String(Date) é
  //    "Mon Jun 01 2026 00:00:00 GMT-0300…", que <input type="date"> rejeita —
  //    o campo aparecia VAZIO ao editar, como se a data não existisse.
  const toInitial = (v: unknown, type?: FieldSpec["type"]) => {
    if (v == null) return "";
    if (type === "date") return toDateInput(v);
    if (type === "datetime") return toDateTimeInput(v);
    return typeof v === "object" ? JSON.stringify(v) : String(v);
  };
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(editable.map((f) => [f.name, toInitial(initial[f.name], f.type)])),
  );
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const viagem = spec.entity === "viagem_vega";
  const motoristaMudou = values.motorista !== toInitial(initial.motorista);
  const motoristaBusca = useDebounced(values.motorista ?? "");
  const frota = useQuery({
    queryKey: ["placas-motorista", motoristaBusca],
    enabled: viagem && motoristaMudou && !!motoristaBusca,
    queryFn: async () => (await api.post("/operacoes/preview", { motorista: motoristaBusca })).data as Record<string, unknown>,
  });
  const buscandoPlacas = viagem && motoristaMudou && !!values.motorista &&
    (motoristaBusca !== values.motorista || frota.isFetching);
  const valueOf = (campo: string) => values[campo] ?? (
    viagem && PLACAS.includes(campo) && motoristaMudou && motoristaBusca === values.motorista
      ? String(frota.data?.[campo] ?? "") : ""
  );
  // O diff da edição precisa serializar o valor original com o MESMO formato
  // do input, senão uma data legítima parece "alterada" e é reenviada à toa.
  const tipoDe = new Map(editable.map((f) => [f.name, f.type]));
  const obrigatorio = new Set(editable.filter((f) => f.required).map((f) => f.name));

  const set = (name: string, v: string) => {
    setPreview(null);
    setValues((s) => {
      const next = { ...s, [name]: v };
      if (viagem && name === "motorista" && v !== s.motorista) {
        for (const campo of PLACAS) {
          // Ausência significa usar a sugestão da frota; vazio explícito
          // significa retirar a placa. Não enviar placas do motorista anterior.
          if (v === toInitial(initial.motorista)) next[campo] = toInitial(initial[campo]);
          else delete next[campo];
        }
      }
      return next;
    });
  };

  // Prévia dos campos calculados (apenas viagem_vega).
  async function runPreview() {
    if (spec.entity !== "viagem_vega") return;
    const payload = Object.fromEntries(editable.map((f) => [f.name, valueOf(f.name) || null]));
    setPreviewError(null);
    try {
      const { data } = await api.post("/operacoes/preview", payload);
      setPreview(data);
    } catch (error) { setPreviewError(error); }
  }

  function handleSubmit() {
    // CRIAÇÃO: manda tudo que foi preenchido (campos obrigatórios valem).
    //
    // EDIÇÃO: manda só o que MUDOU. É um PATCH — e é o que destrava a tela.
    // Reenviar todos os campos fazia qualquer valor legado inválido (ex.:
    // status "carregado" em caixa baixa, vindo da planilha) viajar junto e
    // ser barrado pelo Zod, então trocar só o motorista falhava numa linha
    // que o operador nem tinha tocado. Mandando o diff, valor antigo que não
    // foi editado não é revalidado. Campo esvaziado vira `null` explícito —
    // antes ele sumia do payload e era impossível limpar um campo.
    const data: Record<string, unknown> =
      mode === "create"
        ? Object.fromEntries(Object.entries(values).filter(([, v]) => v !== ""))
        : Object.fromEntries(
            Object.entries(values)
              .filter(([k, v]) => (viagem && motoristaMudou && PLACAS.includes(k)) || v !== toInitial(initial[k], tipoDe.get(k)))
              // Esvaziar vira `null` explícito (permite LIMPAR um campo) —
              // exceto nos obrigatórios: o schema de update torna tudo
              // opcional, então sem esta guarda dava para apagar o nº da ordem.
              // Motorista é opcional: pode ser desvinculado pelo operador.
              .filter(([k, v]) => !(v === "" && obrigatorio.has(k)))
              .map(([k, v]) => [k, v === "" ? null : v]),
          );

    // Uma placa sugerida que foi apagada precisa chegar como null até na
    // criação; omitir o campo faria o servidor recolocar a sugestão.
    if (viagem && mode === "create") {
      for (const campo of PLACAS) {
        if (Object.prototype.hasOwnProperty.call(values, campo) && values[campo] === "") data[campo] = null;
      }
    }

    if (mode === "update" && Object.keys(data).length === 0) {
      onCancel(); // nada mudou: fecha sem bater na API
      return;
    }

    const parsed = buildZodSchema(spec, mode).safeParse(data);
    if (!parsed.success) {
      setErrors(parsed.error.flatten().fieldErrors as Record<string, string[]>);
      return;
    }
    setErrors({});
    onSubmit(parsed.data as Record<string, unknown>);
  }

  return (
    <div className="space-y-4">
      {viagem && (
        <div className="rounded-lg bg-brand-50 p-3 text-sm text-slate-700" data-testid="vinculos-carga">
          <b>Motorista e placas desta carga</b>
          <p>Ao escolher o motorista, as placas da frota são sugeridas. Confira e ajuste o cavalo e as carretas para esta viagem. Os vínculos serão mantidos nas próximas edições.</p>
          {buscandoPlacas && <p className="mt-1 text-xs">Buscando placas do motorista…</p>}
          {motoristaMudou && frota.isError && <p className="mt-1 text-xs text-amber-700">Não foi possível buscar as placas. Preencha os veículos abaixo ou tente novamente.</p>}
        </div>
      )}
      {viagem && <LinkSuggestions target={{numero_ordem:values.numero_ordem??'',cidade_entrega:values.cidade_entrega??'',motorista:values.motorista??'',cavalo:values.cavalo??'',data_hora_coleta:values.data_hora_coleta?values.data_hora_coleta+'-03:00':''}} onPickupChange={v=>set('data_hora_coleta',v)} onSelect={a=>{
        setPreview(null);
        setValues(s=>({...s,motorista:a.motorista,cavalo:a.cavalo,primeira_carreta:a.primeira_carreta,dolly:a.dolly,segunda_carreta:a.segunda_carreta}));
      }}/>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {editable.map((field) => (
          <div key={field.name}>
            <label className="label">{field.label}{field.required && mode === "create" && <span className="text-red-500"> *</span>}</label>
            <Field field={field} value={valueOf(field.name)} onChange={(v) => set(field.name, v)} />
            {errors[field.name] && <p className="mt-1 text-xs text-red-600">{errors[field.name]!.join(", ")}</p>}
          </div>
        ))}
      </div>

      {spec.entity === "viagem_vega" && (
        <div className="rounded-lg bg-slate-50 p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Prévia dos campos calculados</span>
            <button type="button" className="btn-ghost text-xs" onClick={runPreview}>Calcular prévia</button>
          </div>
          {previewError != null && <ErrorBox error={previewError} />}
          {preview && (
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
              {computed.map((c) => (
                <div key={c.name}>
                  <div className="text-xs text-slate-400">{c.label}</div>
                  <div className="font-medium text-slate-700">
                    {c.type === "currency" ? brl(preview[c.name] as number) : String(preview[c.name] ?? "—")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onCancel}>Cancelar</button>
        <button className="btn-primary" onClick={handleSubmit} disabled={submitting || buscandoPlacas}>
          {submitting ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </div>
  );
}
